import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';

import { Store } from '@ngrx/store';

import { Subscription } from 'rxjs';

import { IMqttMessage, MqttService } from 'ngx-mqtt';

import { AppState } from 'src/app/state';
import { Intervention } from 'src/app/models/intervention';
import { selectSelectedIntervention } from 'src/app/dashboard/state/intervention/intervention.selectors';
import { Device } from 'src/app/models/device';
import {
  connectingMqttClientError,
  editDeviceTag,
  getDevice,
  getDevices,
  saveUnusedDevice,
} from '../../../../state/device/device.actions';
import { finishSession } from '../../../../state/intervention/intervention.actions';
import { selectDevices, selectSelectedDevice } from '../../../../state/device/device.selectors';
import { environment } from '../../../../../../environments/environment';
import { PrivilegeService } from '../../../../services/privilege/privilege.service';
import { PRIVILEGE_ACTIONS, PRIVILEGE_SECTIONS } from '../../../../../core/dashboard-constants';

const MQTT_KEYS = {
  PING: 'ping',
  DASHBOARD: 'dashboard',
  RECORD: 'record',
  START: 'start',
  STOP: 'stop',
  ACTIVE: 'active',
  STANDBY: 'standby',
  OFFLINE: 'offline',
  DISCONNECTED: 'disconnected',
};

export const MQTT_MESSAGES = {
  DASHBOARD: 'dashboard',
  DISCONNECTED: 'disconnected',
  DISCONNECTED_DASHBOARD: 'disconnected:dashboard',
  CONNECT_DASHBOARD: 'ping:dashboard',
  START_RECORDING: 'record:start',
  STOP_RECORDING: 'record:stop',
  DISCONNECT_DEVICE: 'disconnect:',
};

@Component({
  selector: 'dashboard-session-status',
  templateUrl: './session-status.component.html',
  styleUrls: ['./session-status.component.scss'],
})
export class SessionStatusComponent implements OnInit, OnDestroy {
  disable = false;

  selectedIntervention: Intervention;
  selectedDevices: Device[];

  sessionDeviceForm: FormGroup;

  devices: Map<string, Device> = new Map<string, Device>();

  tagInputIndex: number = null;
  disableWhileRecording = false;
  showStatusMessage = 'Not connected!';

  topicSubscription: Subscription;
  topicName: string;
  message: string;
  isConnected = false;

  mqttHost = environment.mqtt.host;
  mqttPort = environment.mqtt.port;
  mqttPath = environment.mqtt.path;

  PRIVILEGE_SECTIONS = PRIVILEGE_SECTIONS;
  PRIVILEGE_ACTIONS = PRIVILEGE_ACTIONS;

  private subscriptions = new Subscription();

  constructor(
    private store: Store<AppState>,
    private mqttService: MqttService,
    private formBuilder: FormBuilder,
    public privilegeService: PrivilegeService
  ) {}

  ngOnInit(): void {
    this.sessionDeviceForm = this.formBuilder.group({
      devicesArray: this.formBuilder.array([]),
    });

    this.subscriptions.add(
      this.store.select(selectSelectedIntervention).subscribe((intervention) => {
        this.selectedIntervention = intervention;

        this.checkPrivileges();

        if (!!this.selectedIntervention?.codeExpired) {
          this.disable = true;
        }

        if (intervention && intervention?.devices) {
          this.loadDevicesFromIntervention(intervention);
        }

        if (this.selectedIntervention && !this.selectedIntervention?.startFromPhone) {
          this.connectClient();
          if (this.selectedIntervention?.code) {
            this.subscribeToANewTopic();
          }
        }
      })
    );

    this.subscriptions.add(
      this.store.select(selectSelectedDevice).subscribe((device) => {
        if (device) {
          this.devices.set(device.uuid, device);
        }
      })
    );

    this.subscriptions.add(
      this.store.select(selectDevices).subscribe((devices) => {
        if (devices?.length) {
          this.selectedDevices = devices;
        }
      })
    );

    this.mqttService.onConnect.subscribe((connect) => {
      this.showStatusMessage = 'Dashboard connected. Standing By.';
      this.sendMessage(MQTT_MESSAGES.CONNECT_DASHBOARD);
    });

    this.mqttService.onError.subscribe((error) => {
      this.store.dispatch(connectingMqttClientError({ err: error }));
      this.connectClient();
    });

    if (this.selectedIntervention?.devices.length) {
      this.populateDevicesFormArray();
    }
  }

  get devicesArray(): FormArray {
    return this.sessionDeviceForm.get('devicesArray') as FormArray;
  }

  uintToString(uintArray): string {
    const encodedString = String.fromCharCode.apply(null, uintArray);
    return decodeURIComponent(escape(encodedString));
  }

  subscribeToANewTopic(): void {
    this.topicName = `SM/${this.selectedIntervention.code}`;
    this.topicSubscription = this.mqttService.observe(this.topicName).subscribe((message: IMqttMessage) => {
      this.message = this.uintToString(message.payload);

      const messageKey = this.message.split(':').shift();
      const messageValue = this.message.split(':').pop();

      if (messageKey === MQTT_KEYS.DISCONNECTED) {
        this.devices.set(messageValue, {
          ...this.devices.get(messageValue),
          status: MQTT_KEYS.OFFLINE,
        });
      }

      if (messageKey === MQTT_KEYS.STANDBY || messageKey === MQTT_KEYS.ACTIVE) {
        if (this.devices.get(messageValue)) {
          this.devices.set(messageValue, {
            ...this.devices.get(messageValue),
            status: messageKey,
          });
        } else {
          this.getDevice(messageValue);
        }
      }

      if (this.message === MQTT_MESSAGES.STOP_RECORDING) {
        this.devices.forEach((device) => (device.status = MQTT_KEYS.OFFLINE));
      }

      this.populateDevicesFormArray();
    });
  }

  loadDevicesFromIntervention(intervention: Intervention): void {
    this.devices.clear();
    intervention.devices.forEach((device) => {
      this.devices.set(device.uuid, device);
      if (!intervention.startFromPhone) {
        this.devices.set(device.uuid, {
          ...this.devices.get(device.uuid),
          status: MQTT_KEYS.OFFLINE,
        });
      }
    });
  }

  populateDevicesFormArray(): void {
    this.devicesArray.clear();
    this.devices.forEach((value, key) => {
      this.devicesArray.push(
        this.formBuilder.group({
          key: [key, [Validators.required]],
          tag: [value?.tag || key, [Validators.required]],
          notUsed: [{ value: value.notUsed, disabled: this.disable }, [Validators.required]],
        })
      );
    });
  }

  sendMessage(message: string): void {
    if (!this.selectedIntervention || !this.selectedIntervention?.code) {
      return;
    }
    this.topicName = `SM/${this.selectedIntervention.code}`;
    this.message = message;

    this.mqttService.unsafePublish(this.topicName, this.message, {
      qos: 2,
      retain: false,
    });
  }

  disconnectFromTopic(): void {
    if (this.topicSubscription) {
      this.mqttService.disconnect();
      this.topicSubscription.unsubscribe();
    }
  }

  startRecording(): void {
    this.disable = true;
    this.sendMessage(MQTT_MESSAGES.START_RECORDING);
    this.showStatusMessage = 'Recording!';
    this.disableWhileRecording = true;
  }

  stopRecording(): void {
    this.disable = false;
    this.sendMessage(MQTT_MESSAGES.STOP_RECORDING);
    this.showStatusMessage = 'Stopped!';
    this.disableWhileRecording = false;
  }

  checkPrivileges(): void {
    if (
      this.privilegeService.hasPrivilege(PRIVILEGE_SECTIONS.INTERVENTIONS, PRIVILEGE_ACTIONS.VIEW) &&
      !this.privilegeService.hasPrivilege(PRIVILEGE_SECTIONS.INTERVENTIONS, PRIVILEGE_ACTIONS.ALL)
    ) {
      this.sessionDeviceForm.disable();
    }
  }

  isFilePresent(uniqueDeviceId): boolean {
    let devices;
    if (this.selectedDevices) {
      devices = this.selectedDevices.filter((device) => device.uuid === uniqueDeviceId);
    }
    return devices?.length > 0 && devices[0]?.filename;
  }

  getUploadedFileList(): void {
    this.store.dispatch(getDevices({ intervention: this.selectedIntervention }));
  }

  getDeviceStatus(key: string): string {
    return this.devices.get(key).status ? this.devices.get(key).status : 'Connecting';
  }

  getDevice(uuid: string): void {
    this.store.dispatch(
      getDevice({
        intervention: this.selectedIntervention,
        deviceUniqueId: uuid,
      })
    );
  }

  editDeviceTag(index: number, key: string): void {
    this.tagInputIndex = null;
    const updatedDevice = {
      ...this.devices.get(key),
      tag: this.devicesArray.value[index].tag,
    };

    this.store.dispatch(
      editDeviceTag({
        intervention: this.selectedIntervention,
        device: updatedDevice,
      })
    );
  }

  showTagInput(index: number): void {
    this.tagInputIndex = index;
  }

  sessionFinished(): void {
    const intervention = {
      ...this.selectedIntervention,
      codeExpired: true,
    };
    this.store.dispatch(finishSession({ intervention }));
  }

  saveUnusedDevice(index: number, key: string): void {
    const updatedDevice = {
      ...this.devices.get(key),
      notUsed: this.devicesArray.value[index].notUsed,
    };
    this.store.dispatch(
      saveUnusedDevice({
        interventionId: this.selectedIntervention?.id,
        device: updatedDevice,
      })
    );
    this.sendMessage(`${MQTT_MESSAGES.DISCONNECT_DEVICE}${key}`);
  }

  ngOnDestroy() {
    this.disconnectFromTopic();
    this.subscriptions.unsubscribe();
  }

  private connectClient(): void {
    if (!this.isConnected) {
      this.mqttService.connect({
        hostname: this.mqttHost,
        port: this.mqttPort,
        path: this.mqttPath,
      });
      this.isConnected = true;
    }
  }
}
